// Story 11.4 — Modal mínimo. NO usa shadcn AlertDialog porque las primitivas
// no están scaffoldeadas todavía; el proyecto usa `@radix-ui/react-dialog`
// directo cuando se necesite a11y completa. Este modal es suficiente para
// los dialogs de Deploy/Rollback en MVP — backdrop + Esc para cerrar.

import { X } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Footer con botones, típicamente Cancel / Confirm. */
  footer?: React.ReactNode;
  className?: string;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
}: Props): React.ReactElement | null {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      // Backdrop oscuro mas opaco — `bg-background/70` no contrastaba con
      // el modal `bg-card` porque ambos usan tokens cercanos en oklch.
      // `bg-black/70` da un velo neutro que separa visualmente el modal
      // del shell sin importar el tema.
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        // Esc se maneja en el effect global; este handler satisface a11y
        // (useKeyWithClickEvents) sin duplicar lógica.
        if (e.key === 'Escape') onClose();
      }}
    >
      {/* Nunca más alto que la ventana: header y footer fijos, el cuerpo con
          scroll propio (si no, en pantallas bajas se cortaban los botones). */}
      <div
        className={cn(
          'flex max-h-full w-full max-w-lg flex-col rounded-md border border-border bg-card shadow-xl',
          className,
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-2 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <h2 id="modal-title" className="text-base font-semibold">
              {title}
            </h2>
            {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
          </div>
          <Button
            size="icon"
            variant="ghost"
            onClick={onClose}
            aria-label="Cerrar"
            className="h-7 w-7 shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 text-sm">{children}</div>
        {footer && (
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
